import { Link } from "react-router-dom";
import { ArrowRight, Users, GitBranch, BookOpen, BarChart3, GraduationCap, Briefcase, Trophy, HeartHandshake, Building, Compass, HardHat, Globe2 } from "lucide-react";

const modules = [
  { icon: Users, title: "Diagnostics", desc: "Multi-dimensional EQ self-assessment with AI-generated growth summaries.", tint: "#0B1E36" },
  { icon: GitBranch, title: "Simulations", desc: "Branching leadership scenarios with real-time AI coaching debriefs.", tint: "#1D4ED8" },
  { icon: BookOpen, title: "Microlearning", desc: "3–7 minute lessons personalized to the gaps that matter for you.", tint: "#C9A961" },
  { icon: BarChart3, title: "System Dashboards", desc: "Cohort maturity, trends, and board-ready insights across your org.", tint: "#2C7A7B" },
];

const industries = [
  { icon: GraduationCap, name: "Schools & Districts", desc: "K-12 principals, APs, coaches, and system leaders." },
  { icon: Compass, name: "Colleges & Universities", desc: "Deans, chairs, and student-success teams." },
  { icon: Briefcase, name: "Workforce Development", desc: "Training, upskilling, and talent partners." },
  { icon: HeartHandshake, name: "Community Organizations", desc: "Nonprofit and mission-driven leaders." },
  { icon: HardHat, name: "Labor & Union Partners", desc: "Building leadership capacity across the workforce." },
  { icon: Trophy, name: "Sports Organizations", desc: "Coaches, athletic directors, and performance staff." },
  { icon: Building, name: "Employers & Businesses", desc: "People leaders and executive teams." },
  { icon: Globe2, name: "Global Partners", desc: "International institutions and cross-border programs." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      {/* ═══ Cover hero — single composited image with clickable overlays ═══ */}
      <section className="relative w-full bg-[#03133f]" data-testid="hero-cover">
        <img
          src="https://customer-assets-wrfwihn1.emergentagent.net/job_eq-leadership-hub/artifacts/ef10j33o_85bbd999-b811-4e27-a8c6-cd1de380c408-1.png"
          alt="Future Counts OS™ — The Future Counts Collective. A lightbulb bursting with blue and white smoke on a deep navy background, symbolizing emotional intelligence, leadership, and human potential."
          className="block w-full h-auto select-none"
          data-testid="hero-cover-image"
          loading="eager"
          draggable={false}
        />

        {/* Soft radial navy blob — hides only the baked subtitle & button, leaves baked headline & smoke fully visible */}
        <div
          className="absolute pointer-events-none"
          aria-hidden="true"
          style={{
            top: "48%",
            left: "-4%",
            width: "53%",
            height: "38%",
            background:
              "radial-gradient(ellipse 65% 62% at 42% 50%, #03133f 0%, #03133f 70%, rgba(3,19,63,0.4) 88%, rgba(3,19,63,0) 100%)",
          }}
        />

        {/* Real HTML subtitle, tagline & CTA — positioned below the baked "Future Counts OS™" headline */}
        <div
          className="absolute flex flex-col pointer-events-none"
          style={{
            top: "51%",
            left: 0,
            width: "50%",
            paddingLeft: "4.5%",
            paddingRight: "3%",
          }}
        >
          <div
            className="font-serif font-bold leading-tight"
            style={{ fontSize: "clamp(18px, 1.7vw, 32px)", color: "#7EC1FF" }}
            data-testid="blue-hero-subtitle"
          >
            The Future Counts Collective
          </div>
          <p
            className="mt-4 font-serif italic font-bold leading-snug"
            style={{ fontSize: "clamp(14px, 1.15vw, 22px)", maxWidth: "44rem", color: "#7EC1FF" }}
            data-testid="hero-tagline"
          >
            Building stronger people, stronger teams, and stronger communities through leadership, emotional intelligence, and belonging.
          </p>
          <div className="mt-6 pointer-events-auto">
            <Link
              to="/partners"
              data-testid="blue-hero-cta"
              aria-label="Become an EQ Partner"
              className="inline-flex items-center gap-2 bg-white text-[#0066ff] px-8 py-4 rounded-full font-semibold text-base hover:bg-white/90 shadow-lg"
            >
              Become an EQ Partner <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {/* Transparent clickable overlays for the composited nav */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="false">
          {/* Nav — About */}
          <Link
            to="/about"
            data-testid="hdr-about"
            aria-label="About"
            className="absolute pointer-events-auto"
            style={{ top: "1.6%", left: "47.2%", width: "6.5%", height: "5.5%" }}
          />
          {/* Nav — Become a Partner */}
          <Link
            to="/partners"
            data-testid="hdr-partners"
            aria-label="Become a Partner"
            className="absolute pointer-events-auto"
            style={{ top: "1.6%", left: "55.2%", width: "11.5%", height: "5.5%" }}
          />
          {/* Nav — Contact */}
          <Link
            to="/contact"
            data-testid="hdr-contact"
            aria-label="Contact"
            className="absolute pointer-events-auto"
            style={{ top: "1.6%", left: "68.5%", width: "6.5%", height: "5.5%" }}
          />
          {/* Nav — Sign in */}
          <Link
            to="/login"
            data-testid="hdr-login"
            aria-label="Sign in"
            className="absolute pointer-events-auto"
            style={{ top: "1.6%", left: "76.2%", width: "6.5%", height: "5.5%" }}
          />
          {/* Nav — Get started pill */}
          <Link
            to="/signup"
            data-testid="hdr-signup"
            aria-label="Get started"
            className="absolute pointer-events-auto rounded-full"
            style={{ top: "1.2%", left: "84.4%", width: "10%", height: "6.5%" }}
          />
          {/* Logo — routes to home (self) */}
          <Link
            to="/"
            data-testid="hdr-logo"
            aria-label="Future Counts OS home"
            className="absolute pointer-events-auto"
            style={{ top: "0.8%", left: "2%", width: "27%", height: "7.5%" }}
          />
        </div>
      </section>

      {/* ═══ What We Do — About the Collective + Future Counts OS ═══ */}
      <section className="bg-white" data-testid="what-we-do-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <h2
              className="font-serif text-[#0B1E36] tracking-tight leading-[1.02]"
              style={{ fontSize: "clamp(38px, 4.6vw, 68px)" }}
              data-testid="what-we-do-heading"
            >
              What We Do
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14">
            {/* Left — About the Collective */}
            <div
              className="bg-white p-8 lg:p-10 rounded-2xl border border-slate-200 card-lift"
              data-testid="about-collective-column"
            >
              <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-4">
                The Collective
              </div>
              <h3 className="font-serif text-2xl lg:text-3xl text-[#0B1E36] leading-tight mb-5">
                About the Collective
              </h3>
              <div className="space-y-4 text-[15px] text-slate-700 leading-[1.75]">
                <p>
                  The Future Counts Collective is a movement dedicated to advancing Human Development, Emotional Intelligence, Leadership, Communication, Belonging, and Community Engagement across schools, workplaces, universities, community organizations, and civic institutions.
                </p>
                <p>
                  We believe that success is determined not only by what individuals know, but also by how effectively they communicate, collaborate, lead, build relationships, and contribute to their communities.
                </p>
                <p>
                  The Future Counts Collective was created to bring together educators, workforce development leaders, community organizations, universities, employers, labor partners, athletic organizations, and global partners around a shared commitment to developing the whole person.
                </p>
              </div>
            </div>

            {/* Right — Future Counts OS */}
            <div
              className="bg-[#0B1E36] p-8 lg:p-10 rounded-2xl text-white card-lift"
              data-testid="future-counts-os-column"
            >
              <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-4">
                The Platform
              </div>
              <h3 className="font-serif text-2xl lg:text-3xl leading-tight mb-5">
                Future Counts OS<span className="align-super text-lg">™</span>
              </h3>
              <div className="space-y-4 text-[15px] text-slate-200 leading-[1.75]">
                <p>
                  Future Counts OS™ is the EQ leadership infrastructure that operationalizes emotionally intelligent leadership across K–12 systems, higher-ed institutions, and organizations.
                </p>
                <p>
                  Through <span className="text-[#C9A961] font-semibold">diagnostics, scenario-based simulations, personalized microlearning,</span> and <span className="text-[#C9A961] font-semibold">system dashboards</span>, we help leaders measure and strengthen the human skills that drive performance, culture, belonging, and long-term success.
                </p>
                <p className="italic text-slate-300 pt-3 border-t border-white/15">
                  The Collective is the <span className="text-[#C9A961] not-italic font-semibold">who</span>. Future Counts OS™ is the <span className="text-[#C9A961] not-italic font-semibold">how</span>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ Meet Karen Pinckney ═══ */}
      <section className="bg-white" data-testid="meet-karen-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Circular photo */}
            <div className="lg:col-span-5 flex justify-center lg:justify-start">
              <div
                className="relative w-64 h-64 md:w-80 md:h-80 lg:w-[22rem] lg:h-[22rem] rounded-full overflow-hidden ring-[6px] ring-[#0B1E36]"
                style={{ boxShadow: "0 30px 60px -20px rgba(11,30,54,0.35)" }}
              >
                <img
                  src="https://customer-assets-cm19k8pv.emergentagent.net/wingman/61d87826-79cc-4069-971a-69805bdc11e9/attachments/16ec98927637422a85468c889dd89499_tdemudtj_Karen%20Pinckney%20Website%20Pic.png"
                  alt="Karen Pinckney, Founder of the Future Counts Collective"
                  className="w-full h-full object-cover"
                  data-testid="karen-photo"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Text + CTA */}
            <div className="lg:col-span-7">
              <h2
                className="font-serif text-[#0B1E36] tracking-tight leading-[1.02]"
                style={{ fontSize: "clamp(38px, 4.6vw, 68px)" }}
                data-testid="karen-eyebrow"
              >
                Meet the Founder
              </h2>
              <h3
                className="mt-4 font-serif text-[#0B1E36] tracking-tight leading-[1.1]"
                style={{ fontSize: "clamp(24px, 2.4vw, 36px)" }}
                data-testid="karen-name"
              >
                Karen Pinckney
              </h3>
              <div className="mt-2 text-sm md:text-base text-slate-600 font-medium" data-testid="karen-title">
                Founder <span className="text-slate-300 mx-2">•</span> Author <span className="text-slate-300 mx-2">•</span> Speaker
              </div>

              <div className="mt-6 space-y-4 text-slate-700 leading-relaxed text-[15px] md:text-base" data-testid="karen-bio">
                <p>
                  For more than 30 years, Karen Pinckney has had a front-row seat to how people, teams, and organizations navigate challenges and succeed. Drawing on three decades of experience across higher education, corporate leadership, and education, Karen helps leaders develop the human skills that drive performance, strengthen culture, foster belonging, and create long-term success.
                </p>
                <p>
                  Karen partners with decision-makers across education, business, athletics, and community organizations to build stronger people, stronger teams, and stronger communities by helping leaders develop the human skills that drive performance, strengthen culture, and create lasting impact.
                </p>
                <p className="italic font-semibold text-[#0B1E36]">
                  Building stronger people, stronger teams, and stronger communities through leadership, emotional intelligence, and belonging.
                </p>
              </div>

              <div className="mt-8">
                <Link
                  to="/contact"
                  data-testid="karen-cta"
                  aria-label="Let's Work Together"
                  className="inline-flex items-center gap-2 bg-[#0B1E36] text-white px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-[#132a4a] btn-lift"
                >
                  Let's Work Together <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-[#f4f4f4]" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-widest text-[#0B1E36] font-semibold mb-6" data-testid="mission-eyebrow">
                Our mission
              </div>
              <p className="text-[17px] lg:text-[18px] text-[#0B1E36] leading-[1.75] font-serif" data-testid="mission-copy">
                We believe the future is shaped by who people are becoming. The Future Counts Collective exists to advance emotional intelligence, belonging, leadership, and human development in schools, workplaces, and communities. We partner with individuals and organizations to build the skills, relationships, and cultures that help people thrive. Because when people feel valued, connected, and empowered, they create stronger families, stronger communities, and a better future for all. The future counts because people count.
              </p>
              <div className="mt-10">
                <div
                  className="text-[#0B1E36] leading-none"
                  style={{
                    fontFamily: "'Great Vibes', cursive",
                    fontWeight: 400,
                    fontSize: "76px",
                    letterSpacing: "0.5px",
                    display: "inline-block",
                    lineHeight: 1,
                    paddingBottom: "6px",
                  }}
                  data-testid="mission-signature"
                >
                  Karen Pinckney
                </div>
                <div className="text-sm text-slate-600 mt-3">
                  <span className="font-semibold text-[#0B1E36]">Karen Pinckney</span> · Founder & Executive Director · The Future Counts Collective
                </div>
              </div>
            </div>
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <div
                className="relative w-full max-w-[440px] aspect-square rounded-full overflow-hidden bg-white"
                style={{ boxShadow: "0 0 0 4px #0B1E36, 0 30px 60px -20px rgba(11,30,54,0.30)" }}
                data-testid="mission-photo"
              >
                <img
                  src="https://customer-assets-wrfwihn1.emergentagent.net/job_eq-leadership-hub/artifacts/tdemudtj_Karen%20Pinckney%20Website%20Pic.png"
                  alt="Karen Pinckney, Founder & Executive Director"
                  className="h-full w-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ Final CTA — Let's Start a Conversation ═══ */}
      <section className="bg-white" data-testid="lets-connect-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20 lg:py-24 text-center">
          <h2
            className="font-serif text-[#0B1E36] tracking-tight leading-[1.02]"
            style={{ fontSize: "clamp(38px, 4.6vw, 68px)" }}
            data-testid="lets-connect-heading"
          >
            Let's Start a Conversation
          </h2>
          <p className="mt-5 text-slate-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Whether you're leading a school, a team, or a community — there's a way to build stronger people, stronger teams, and stronger communities together.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              data-testid="cta-schedule-conversation"
              aria-label="Schedule a Conversation"
              className="inline-flex items-center gap-2 bg-[#0B1E36] text-white px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-[#132a4a] btn-lift"
            >
              Schedule a Conversation <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="cta-book-karen"
              aria-label="Book Karen to Speak"
              className="inline-flex items-center gap-2 bg-white border-2 border-[#0B1E36] text-[#0B1E36] px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-[#0B1E36] hover:text-white btn-lift"
            >
              Book Karen to Speak <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/partners"
              data-testid="cta-become-partner"
              aria-label="Become an EQ Partner"
              className="inline-flex items-center gap-2 bg-[#0066ff] text-white px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-[#0053cf] btn-lift"
            >
              Become an EQ Partner <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between text-xs text-slate-500">
          <div>© 2026 Future Counts OS™</div>
          <div>Built for the humans leading learning.</div>
        </div>
      </footer>
    </div>
  );
}
